import {
  AddReservationRequestDto,
  AutoCompleteResponse,
  HotelInfoRequestDto,
  HotelRatesCalendarRequestDto,
  HotelRatesRequestDto,
  HotelSearchAutoCompleteDto,
  HotelSearchRecommendedDto,
  IResponse,
  IRocketTokens,
  OrganisationUpdateDto,
  ProfileDto,
  ProtoUser,
  RequestAccessDto,
  SERPRegionRequestDto,
  SPBookingFormRequestDto,
  SPBookingFormResponseDto,
  SPHotelInfo,
  SPHotelInfoShort,
  SPHotelRateCalendar,
  SPHotelRates,
  SPPrebookRequestDto,
  SPPrebookResponseDto,
  SPSearchResp,
  TierTeam,
  UpdateUserDTO,
} from "@eventr/common";
import { AxiosRequestConfig } from "axios";
import { LngLat } from "maplibre-gl";
import { requestsItinerary } from "./api";
import { currentHotelSite } from "../feat/hotelBooking/hotel-site";
import { gwApiClient } from "./apiClients";

interface CacheEntry<T> {
  timestamp: number;
  data: T;
}

const cache: { [key: string]: CacheEntry<AutoCompleteResponse> } = {};

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_DURATION;
}

export const EventrGatewayAPI = {
  profile: {
    getMe: async (config?: AxiosRequestConfig<any>): Promise<IResponse<ProfileDto>> => {
      return gwApiClient.get("profile/me", {
        ...config,
      });
    },
    update: async (body: UpdateUserDTO): Promise<IResponse<ProfileDto>> => {
      return gwApiClient.post("profile", body);
    },
    /** Sets the user's hotel rewards choice for the current organisation. null follows the org default. */
    setHotelRewards: async (hotelRewardsOnly: boolean | null): Promise<IResponse<ProfileDto>> => {
      return gwApiClient.post("profile/hotelRewards", { hotelRewardsOnly });
    },
    delete: async (): Promise<IResponse<any>> => {
      return gwApiClient.post("/profile/deleteRequest", {});
    },
    forgotPassword: async (email: string): Promise<IResponse<any>> => {
      return gwApiClient.post("/profile/forgotPassword", { email });
    },
    exists: async (email: string): Promise<IResponse<boolean>> => {
      return gwApiClient.get(`/profile/exists/${email.toLowerCase()}`);
    },
    createUser: async (body: ProtoUser): Promise<IResponse<any>> => {
      return gwApiClient.post("profile/createUser", body);
    },
    removeMember: async (wpUserId: number, orgID: string): Promise<IResponse<string>> => {
      return gwApiClient.post(`profile/removeOrg/${wpUserId}?orgId=${orgID}`, {});
    },
    profileShare: {
      fetchAdditionalInfoFields: async (): Promise<IResponse<any>> => {
        return requestsItinerary.get<any>("config/additionalUserInfo", {
          NOT_IN_GUEST: true,
        });
      },
    },
  },
  organisation: {
    syncSubscription: async (orgId: string): Promise<IResponse<any>> => {
      return await gwApiClient.get(`/org/syncSubscription?orgId=${orgId}`);
    },
    updateDetails: async (body: OrganisationUpdateDto): Promise<IResponse<any>> => {
      return gwApiClient.post<OrganisationUpdateDto, any>(`/org/updateDetails`, body);
    },
    fakeSub: async (orgId: string, hours: string, tier: string = TierTeam.id): Promise<IResponse<any>> => {
      return await gwApiClient.get(`/org/fakeSub?orgId=${orgId}&hours=${hours}&tier=${tier}`);
    },
  },

  orgs: {
    getCodeInfo: async (code: string, populated = false): Promise<IResponse<any>> => {
      return gwApiClient.get(`orgsService/${code}/${populated}`);
    },
    getShareCode: async (code: string, orgID: string): Promise<IResponse<any>> => {
      return gwApiClient.get(`shareCodeInfo/${code}/${orgID}`);
    },
  },
  booking: {
    hotel: {
      autocomplete: async (body: HotelSearchAutoCompleteDto): Promise<IResponse<AutoCompleteResponse>> => {
        const cacheKey = JSON.stringify(body);

        // Check if the cache is valid
        if (cache[cacheKey] && isCacheValid(cache[cacheKey].timestamp)) {
          return { data: cache[cacheKey].data, status: "success" };
        }

        // Make the API call if cache is not valid
        const response = await gwApiClient.post<HotelSearchAutoCompleteDto, AutoCompleteResponse>(
          `/booking/hotel/autocomplete`,
          body,
        );

        if (response.status === "success") {
          // Store the result in the cache
          cache[cacheKey] = {
            timestamp: Date.now(),
            data: response.data!,
          };
        }

        return response;
      },
      SERPregion: async (
        body: SERPRegionRequestDto & { point?: [number, number] },
      ): Promise<IResponse<SPSearchResp>> => {
        return gwApiClient.post<SERPRegionRequestDto, SPSearchResp>(
          `/booking/hotel/search/serp/region`,
          { ...body, hotelSite: currentHotelSite() },
          { timeout: 60000 }, // this may take a while
        );
      },
      hotelSearchRecommended: async (body: HotelSearchRecommendedDto): Promise<IResponse<SPHotelInfoShort[]>> => {
        return gwApiClient.post<HotelSearchRecommendedDto, SPHotelInfoShort[]>(
          `/booking/hotel/search/recommended`,
          body,
          { timeout: 20000 },
        );
      },
      hotelInfo: async (body: HotelInfoRequestDto): Promise<IResponse<SPHotelInfo>> => {
        return gwApiClient.post<HotelInfoRequestDto, SPHotelInfo>(`/booking/hotel/info`, body, { timeout: 20000 });
      },
      hotelRates: async (body: HotelRatesRequestDto): Promise<IResponse<SPHotelRates>> => {
        return gwApiClient.post<HotelRatesRequestDto, SPHotelRates>(
          `/booking/hotel/rates`,
          { ...body, hotelSite: currentHotelSite() },
          { timeout: 60000 },
        );
      },
      hotelRatesCalendar: async (body: HotelRatesCalendarRequestDto): Promise<IResponse<SPHotelRateCalendar>> => {
        return gwApiClient.post<HotelRatesCalendarRequestDto, SPHotelRateCalendar>(
          `/booking/hotel/ratecalendar`,
          body,
          { timeout: 30000 },
        );
      },
      prebook: async (body: SPPrebookRequestDto): Promise<IResponse<SPPrebookResponseDto>> => {
        return gwApiClient.post<SPPrebookRequestDto, SPPrebookResponseDto>(`/booking/hotel/prebook`, {
          ...body,
          hotelSite: currentHotelSite(),
        });
      },
      bookForm: async (body: SPBookingFormRequestDto): Promise<IResponse<SPBookingFormResponseDto>> => {
        return gwApiClient.post<SPBookingFormRequestDto, SPBookingFormResponseDto>(`/booking/hotel/bookform`, body);
      },
      addReservation: async (body: AddReservationRequestDto): Promise<IResponse<any>> => {
        return gwApiClient.post<AddReservationRequestDto, any>(`/booking/hotel/addReservation`, {
          ...body,
          hotelSite: currentHotelSite(),
        });
      },
    },
  },
  utils: {
    setCache: async (cacheValue: any): Promise<IResponse<any>> => {
      return gwApiClient.post("/utils/cache", cacheValue);
    },
  },
  address: {
    findAddress: async (address: string): Promise<IResponse<any>> => {
      return gwApiClient.get(`/addresslookup/v2?q=${address}&lang=en`);
    },
    reverse: async (lngLat: LngLat): Promise<IResponse<any>> => {
      const url = `/addresslookup/v2?reverse=true&lat=${lngLat.lat}&lon=${lngLat.lng}&format=json&namedetails=1`;
      return gwApiClient.get(url);
    },
  },
  eco: {
    // Road distance and driving time between two points, from eco-service's GraphHopper. Both come from
    // one route on the car profile. road_km is null when it could not be routed; there is no straight-
    // line fallback, so the mileage form asks the user to type the distance instead. road_minutes is
    // null alongside it, or on its own when the route came back with no readable duration.
    routeDistance: async (
      from: { lat: number; lon: number },
      to: { lat: number; lon: number },
    ): Promise<IResponse<{ road_km: number | null; road_minutes: number | null }>> => {
      return gwApiClient.get("/eco/route/distance", {
        params: { from_lat: from.lat, from_lon: from.lon, to_lat: to.lat, to_lon: to.lon },
      });
    },
  },
  rocketchat: {
    getRocketchatToken: async (): Promise<IResponse<IRocketTokens[]>> => {
      return gwApiClient.get("/rocketchat/tokens", { NOT_IN_GUEST: true });
    },
    downloadFile: async (url: string, userId: string, token: string): Promise<IResponse<any>> => {
      return gwApiClient.get("rocketchat/downloadFile", {
        params: {
          userId: userId,
          token: token,
          fileUrl: url,
        },
      });
    },
    ensureRunning: async (orgID: string): Promise<IResponse<any>> => {
      return gwApiClient.get(`/rocketchat/ensureRunning?orgId=${orgID}`, {
        NOT_IN_GUEST: true,
      });
    },
  },
  media: {
    upload: async (formData: FormData, org?: string): Promise<IResponse<any>> => {
      return gwApiClient.post(`media?orgId=${org}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600_000,
      });
    },
  },
  requestAccess: {
    get: async (orgId?: string): Promise<IResponse<any>> => {
      // Scoped to one organisation: the gateway otherwise reads the join requests of every
      // organisation the caller manages, one WordPress round trip each, and we keep one.
      return gwApiClient.get("/requestAccess", { params: { orgId } });
    },
    approveRequest: async (requestID: string, org: string): Promise<IResponse<any>> => {
      return gwApiClient.post(`/requestAccess/approve/${requestID}?orgId=${org}`, {});
    },
    rejectRequest: async (requestID: string, org: string, reason: string): Promise<IResponse<any>> => {
      return gwApiClient.post(`/requestAccess/reject/${requestID}?orgId=${org}`, { reason });
    },
    request: async (body: RequestAccessDto): Promise<IResponse<any>> => {
      return gwApiClient.post(`/requestAccess`, body);
    },
    requestItinAccess: async (body: RequestAccessDto): Promise<IResponse<any>> => {
      return gwApiClient.post(`/requestItineraryAccess`, body);
    },
  },
  registration: {
    confirmInvite: async (orgID: string, inviteToken: string): Promise<IResponse<any>> => {
      return gwApiClient.post(`invite/confirm/${inviteToken}?orgId=${orgID}`, {});
    },
  },
};
